import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Save, X } from "lucide-react";
import { useState } from "react";

import {
  actualizarContraparte,
  crearContraparte,
  getContrapartes,
  type Contraparte,
  type ContraparteInput,
} from "@/lib/api";

const EMPTY: ContraparteInput = { nombre: "", alias: "", id_cuenta_sim: "", cuenta_orden: "", notas: "" };

export function ContrapartesPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["contrapartes"], queryFn: getContrapartes });

  const [editId, setEditId] = useState<number | null>(null); // null = no edit; 0 = nueva
  const [form, setForm] = useState<ContraparteInput>(EMPTY);

  const reset = () => {
    setEditId(null);
    setForm(EMPTY);
  };

  const mut = useMutation({
    mutationFn: (p: { id: number | null; data: ContraparteInput }) =>
      p.id && p.id > 0 ? actualizarContraparte(p.id, p.data) : crearContraparte(p.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contrapartes"] });
      reset();
    },
  });

  const items = q.data ?? [];

  const editar = (c: Contraparte) => {
    setEditId(c.id_contraparte);
    setForm({
      nombre: c.nombre,
      alias: c.alias ?? "",
      id_cuenta_sim: c.id_cuenta_sim ?? "",
      cuenta_orden: c.cuenta_orden ?? "",
      notas: c.notas ?? "",
    });
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Contrapartes</h1>
          <p className="text-sm text-slate-500 mt-1">
            Catálogo con los identificadores CENACE/SIM. Los contratos eligen de aquí.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setEditId(0); setForm(EMPTY); }}
          className="inline-flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
        >
          <Plus className="w-4 h-4" /> Nueva
        </button>
      </div>

      {editId != null && (
        <div className="bg-white border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">
              {editId > 0 ? "Editar contraparte" : "Nueva contraparte"}
            </h2>
            <button type="button" onClick={reset} className="text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Nombre (razón social)">
              <input className="border rounded px-2 py-1.5 w-full text-sm" value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="GENERACIÓN INDUSTRIAL, S.A. DE C.V.(C059)" />
            </Field>
            <Field label="Nombre corto (alias) — se usa en reportes/gráficas">
              <input className="border rounded px-2 py-1.5 w-full text-sm" value={form.alias ?? ""}
                onChange={(e) => setForm({ ...form, alias: e.target.value })}
                placeholder="GIN" />
            </Field>
            <Field label="ID cuenta SIM (ID_CUENTA_CONTRAPARTE)">
              <input className="border rounded px-2 py-1.5 w-full text-sm" value={form.id_cuenta_sim ?? ""}
                onChange={(e) => setForm({ ...form, id_cuenta_sim: e.target.value })} />
            </Field>
            <Field label="Cuenta de orden (CUENTA_ORDEN_CONTRAPARTE)">
              <input className="border rounded px-2 py-1.5 w-full text-sm" value={form.cuenta_orden ?? ""}
                onChange={(e) => setForm({ ...form, cuenta_orden: e.target.value })} placeholder="C059001" />
            </Field>
            <Field label="Notas">
              <input className="border rounded px-2 py-1.5 w-full text-sm" value={form.notas ?? ""}
                onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            </Field>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => mut.mutate({ id: editId, data: form })}
              disabled={!form.nombre || mut.isPending}
              className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50">
              <Save className="w-4 h-4" /> {mut.isPending ? "Guardando…" : "Guardar"}
            </button>
            {mut.isError && <span className="text-sm text-red-600">{(mut.error as Error).message}</span>}
          </div>
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Nombre</th>
              <th className="px-3 py-2 font-medium">Alias</th>
              <th className="px-3 py-2 font-medium">ID cuenta SIM</th>
              <th className="px-3 py-2 font-medium">Cuenta de orden</th>
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Cargando…</td></tr>}
            {!q.isLoading && items.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Sin contrapartes. Crea la primera.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id_contraparte} className="border-t hover:bg-slate-50">
                <td className="px-3 py-2 font-medium text-slate-800">{c.nombre}</td>
                <td className="px-3 py-2 text-slate-600">{c.alias ?? "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{c.id_cuenta_sim ?? "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{c.cuenta_orden ?? "—"}</td>
                <td className="px-3 py-2 text-right">
                  <button type="button" onClick={() => editar(c)}
                    className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1 hover:bg-slate-100">
                    <Pencil className="w-3 h-3" /> Editar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
